"use client";

import { c as _c } from "react/compiler-runtime";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { DRAG_DROP_PASTE } from "@lexical/rich-text";
import { isMimeType, mediaFileReader } from "@lexical/utils";
import type { EditorState } from "lexical";
import { $getNodeByKey, $insertNodes, COMMAND_PRIORITY_LOW } from "lexical";
import { useEffect, useLayoutEffect, useRef } from "react";
import { $createImageNode } from "../nodes/ImageNode";
import { multiPartUpload } from "@/components/waves/create-wave/services/multiPartUpload";
import { useAuth } from "@/components/auth/Auth";
import { ACCEPTED_FILE_TYPE_LABELS, isSupportedUploadFile } from "@/services/uploads/mediaUploadMimeType";
const ACCEPTABLE_IMAGE_TYPES = ["image/", "image/heic", "image/heif", "image/gif", "image/webp"];
const INLINE_IMAGE_UPLOAD_TIMEOUT_MS = 30_000;
function isAcceptableAttachment(file: File): boolean {
  return isSupportedUploadFile(file) && !isMimeType(file, ACCEPTABLE_IMAGE_TYPES);
}
function withTimeout<T>(run: (signal: AbortSignal) => Promise<T>, timeoutMs: number, message: string): Promise<T> {
  let timeoutId: ReturnType<typeof globalThis.setTimeout> | undefined;
  const controller = new AbortController();
  const timeoutPromise = new Promise<never>((_resolve, reject) => {
    timeoutId = globalThis.setTimeout(() => {
      controller.abort();
      reject(new Error(message));
    }, timeoutMs);
  });
  return Promise.race([run(controller.signal), timeoutPromise]).finally(() => {
    if (timeoutId !== undefined) {
      globalThis.clearTimeout(timeoutId);
    }
  });
}
async function uploadImage(file: File): Promise<string> {
  const multiPart = await withTimeout(signal => multiPartUpload({
    file,
    path: "drop",
    signal
  }), INLINE_IMAGE_UPLOAD_TIMEOUT_MS, "Image upload timed out. Please try again.");
  return multiPart.url;
}
export default function DragDropPaste(t0) {
  const $ = _c(13);
  const {
    disabled: t1,
    onAttachmentFiles,
    onUploadEditorStateChange
  } = t0;
  const disabled = t1 === undefined ? false : t1;
  const {
    setToast
  } = useAuth();
  const onAttachmentFilesRef = useRef(onAttachmentFiles);
  const onUploadEditorStateChangeRef = useRef(onUploadEditorStateChange);
  const disabledRef = useRef(disabled);
  let t2;
  let t3;
  if ($[0] !== onAttachmentFiles) {
    t2 = () => {
      onAttachmentFilesRef.current = onAttachmentFiles;
    };
    t3 = [onAttachmentFiles];
    $[0] = onAttachmentFiles;
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[3] !== onUploadEditorStateChange) {
    t4 = () => {
      onUploadEditorStateChangeRef.current = onUploadEditorStateChange;
    };
    t5 = [onUploadEditorStateChange];
    $[3] = onUploadEditorStateChange;
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  useLayoutEffect(t4, t5);
  let t6;
  let t7;
  if ($[6] !== disabled) {
    t6 = () => {
      disabledRef.current = disabled;
    };
    t7 = [disabled];
    $[6] = disabled;
    $[7] = t6;
    $[8] = t7;
  } else {
    t6 = $[7];
    t7 = $[8];
  }
  useLayoutEffect(t6, t7);
  const [editor] = useLexicalComposerContext();
  let t8;
  let t9;
  if ($[9] !== editor || $[10] !== setToast) {
    t8 = () => {
      let isMounted = true;
      const syncUploadEditorStateWhenDisabled = () => {
        if (!disabledRef.current) {
          return;
        }
        onUploadEditorStateChangeRef.current?.(editor.getEditorState());
      };
      const unregister = editor.registerCommand(DRAG_DROP_PASTE, files => {
        if (disabledRef.current) {
          return true;
        }
        (async () => {
          const filesResult = await mediaFileReader(files, [ACCEPTABLE_IMAGE_TYPES].flatMap(_temp));
          if (disabledRef.current || !isMounted) {
            return;
          }
          const currentOnAttachmentFiles = onAttachmentFilesRef.current;
          const attachmentFiles = currentOnAttachmentFiles ? files.filter(isAcceptableAttachment) : [];
          if (attachmentFiles.length > 0) {
            currentOnAttachmentFiles?.(attachmentFiles);
          }
          if (filesResult.length === 0 && attachmentFiles.length === 0) {
            setToast({
              message: `Unsupported file type. Accepted Types: ${ACCEPTED_FILE_TYPE_LABELS}`,
              type: "error"
            });
            return;
          }
          for (const {
            file
          } of filesResult) {
            if (isMimeType(file, ACCEPTABLE_IMAGE_TYPES)) {
              editor.update(() => {
                const imageNode = $createImageNode({
                  src: "loading"
                });
                $insertNodes([imageNode]);
                const key = imageNode.getKey();
                uploadImage(file).then(url => {
                  if (!isMounted) {
                    return;
                  }
                  let replacedLoadingImage = false;
                  editor.update(() => {
                    const node = $getNodeByKey(key);
                    if (node) {
                      node.replace($createImageNode({
                        src: url
                      }));
                      replacedLoadingImage = true;
                    }
                  }, {
                    onUpdate: () => {
                      if (replacedLoadingImage) {
                        syncUploadEditorStateWhenDisabled();
                      }
                    }
                  });
                }).catch(err => {
                  if (!isMounted) {
                    return;
                  }
                  let removedLoadingImage = false;
                  editor.update(() => {
                    const node_0 = $getNodeByKey(key);
                    if (node_0) {
                      node_0.remove();
                      removedLoadingImage = true;
                    }
                  }, {
                    onUpdate: () => {
                      if (removedLoadingImage) {
                        syncUploadEditorStateWhenDisabled();
                      }
                    }
                  });
                  setToast({
                    message: err instanceof Error ? err.message : "Error uploading image. Please try again.",
                    type: "error"
                  });
                });
              });
            }
          }
        })();
        return true;
      }, COMMAND_PRIORITY_LOW);
      return () => {
        unregister();
        isMounted = false;
      };
    };
    t9 = [editor, setToast];
    $[9] = editor;
    $[10] = setToast;
    $[11] = t8;
    $[12] = t9;
  } else {
    t8 = $[11];
    t9 = $[12];
  }
  useEffect(t8, t9);
  return null;
}
function _temp(x) {
  return x;
}